/**
 * 오프라인 다운로드 HTML 의 `<img>` 인라인 헬퍼.
 *
 * 라이브 화면의 이미지가 `/icons/...` 같은 절대경로(public asset)를 가리키면, file:// 로 열리는
 * 다운로드 파일에선 깨진다. 컴포넌트가 공유라 prop 으로 data URI 를 내려주기 어려운 경우,
 *   - export 시점: collectImageDataUris() 로 라이브 DOM 의 절대경로 이미지를 data URI 로 수집해 임베드
 *   - 뷰어 시점: installImageInliner(map) 로 렌더된 img.src 를 data URI 로 치환(재렌더에도 안전)
 * 하는 식으로 self-contained 화한다.
 */

import { fetchAssetAsDataUri } from "@/components/compare/download/extract-styles";

/** raw src 가 같은 출처의 절대경로 public asset 인가(외부 URL/데이터 URI 제외). */
function isInlinablePath(src: string | null): src is string {
  return !!src && src.startsWith("/") && !src.startsWith("//");
}

/**
 * (export 시점) 현재 문서에 렌더된 모든 `<img>` 중 절대경로 src 를 data URI 로 수집한다.
 * 반환 맵의 키는 원본 src 문자열 그대로(뷰어에서 exact match 로 치환).
 */
export async function collectImageDataUris(): Promise<Record<string, string>> {
  const srcs = new Set<string>();
  for (const img of Array.from(document.querySelectorAll("img"))) {
    const src = img.getAttribute("src");
    if (isInlinablePath(src)) srcs.add(src);
  }
  const entries = await Promise.all(
    Array.from(srcs).map(async (src) => {
      const dataUri = await fetchAssetAsDataUri(src);
      return dataUri ? ([src, dataUri] as const) : null;
    }),
  );
  return Object.fromEntries(entries.filter((e): e is [string, string] => !!e));
}

/**
 * (뷰어 시점) 렌더된 `<img>` 의 src 가 맵에 있으면 data URI 로 치환한다.
 * 초기 sweep + MutationObserver 로 React 재렌더/지연 마운트에도 대응.
 * (치환값은 data URI 라 맵의 키가 아니므로 재치환 루프가 생기지 않는다.)
 */
export function installImageInliner(map: Record<string, string>): void {
  if (!map || Object.keys(map).length === 0) return;

  const rewrite = (img: HTMLImageElement) => {
    const src = img.getAttribute("src");
    if (src && map[src]) img.setAttribute("src", map[src]);
  };
  const rewriteWithin = (node: Node) => {
    if (node instanceof HTMLImageElement) rewrite(node);
    else if (node instanceof HTMLElement)
      node.querySelectorAll("img").forEach(rewrite);
  };

  document.querySelectorAll("img").forEach(rewrite);

  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.type === "attributes" && m.target instanceof HTMLImageElement) {
        rewrite(m.target);
      }
      m.addedNodes.forEach(rewriteWithin);
    }
  });
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["src"],
  });
}
